import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Spinner } from '../../_core/spinner';
import { AuthService } from '../../_core/auth.service';
import { ToastService } from '../../_core/toast.service';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { RouterLink } from '@angular/router';
import { BrandingService } from '../../_core/branding.service';

@Component({
  selector: 'app-account',
  imports: [
    ButtonModule,
    CardModule,
    FloatLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    InputIconModule,
    IconFieldModule,
    ReactiveFormsModule,
    CommonModule,
    RouterLink,
  ],
  templateUrl: './account.html',
  styleUrl: './account.scss',
})
export class Account implements OnInit {
  private $auth = inject(AuthService);
  private $branding = inject(BrandingService);
  private $toast = inject(ToastService);
  private $spinner = inject(Spinner);
  private fBuilder = inject(FormBuilder);

  public readonly sendRequestsEnabled = this.$branding.sendRequestsEnabled;

  public username: string = '';
  public lastAccessDate: string = '';
  public passwordForm: FormGroup = this.initPassForm();
  public showCPassword: boolean = false;
  public showPassword: boolean = false;

  async ngOnInit(): Promise<void> {
    this.username = this.$auth.userData()!.username;
    this.lastAccessDate = this.$auth.userData()!.lastAccessDate;
  }

  public async changePassword(): Promise<void> {
    if (this.passwordForm.invalid) {
      return;
    }
    this.$spinner.show();
    const DATA = {
      currentPassword: this.passwordForm.controls['currentPassword'].value,
      newPassword: this.passwordForm.controls['newPassword'].value,
    };
    try {
      const isChanged = await this.$auth.changePassword(DATA);

      if (isChanged === true) {
        this.$spinner.hide();
        this.$toast.success('Contraseña cambiada con éxito');
      } else {
        this.$spinner.hide();
        this.$toast.error('No se pudo cambiar la contraseña');
      }
    } catch (err) {
      this.$spinner.hide();
      this.$toast.error('No se pudo cambiar la contraseña');
    }
  }

  private initPassForm(): FormGroup {
    return this.fBuilder.group(
      {
        currentPassword: ['', Validators.required],
        newPassword: ['', [Validators.required, PasswordValidators.strength()]],
        repeatPassword: ['', [Validators.required, PasswordValidators.strength()]],
      },
      { validators: PasswordValidators.match('newPassword', 'repeatPassword') },
    );
  }

  get passErrors() {
    const control = this.passwordForm.get('newPassword');

    if (!control || !control.value) {
      return {};
    }

    if (control.valid) {
      return {
        hasMinLength: true,
        hasMaxLength: true,
        hasUpper: true,
        hasLower: true,
        hasNumber: true,
        hasSymbol: true,
      };
    }

    return control.errors?.['passwordStrength'] || {};
  }

  get passwordsMatch() {
    return (
      !this.passwordForm.hasError('passwordsMatch') &&
      this.passwordForm.get('repeatPassword')?.value !== ''
    );
  }
}

export class PasswordValidators {
  static strength(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value || '';
      if (!value) return null;

      const hasMinLength = value.length >= 8;
      const hasMaxLength = value.length <= 10;
      const hasUpper = /[A-Z]/.test(value);
      const hasLower = /[a-z]/.test(value);
      const hasNumber = /\d/.test(value);
      const hasSymbol = /[^a-zA-Z0-9]/.test(value);

      const isValid =
        hasMinLength && hasMaxLength && hasUpper && hasLower && hasNumber && hasSymbol;

      return !isValid
        ? {
            passwordStrength: {
              hasMinLength,
              hasMaxLength,
              hasUpper,
              hasLower,
              hasNumber,
              hasSymbol,
            },
          }
        : null;
    };
  }

  static match(controlName: string, checkControlName: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const password = group.get(controlName)?.value;
      const repeat = group.get(checkControlName)?.value;

      const match = password === repeat && password?.length > 0;

      if (!match) {
        group.get(checkControlName)?.setErrors({ passwordsMatch: true });
        return { passwordsMatch: true };
      } else {
        const errors = group.get(checkControlName)?.errors;
        if (errors) {
          delete errors['passwordsMatch'];
          if (Object.keys(errors).length === 0) group.get(checkControlName)?.setErrors(null);
        }
        return null;
      }
    };
  }
}
